<script setup lang="ts">
import { Icon } from '@engage-web-ui/icon';
import { BillingPricingId } from '@engage-web/product';
import { format } from 'date-fns';
import PrimeAccordion from 'primevue/accordion';
import PrimeAccordionContent from 'primevue/accordioncontent';
import PrimeAccordionHeader from 'primevue/accordionheader';
import PrimeAccordionPanel from 'primevue/accordionpanel';
import PrimeButton from 'primevue/button';
import PrimeSelectButton from 'primevue/selectbutton';
import PrimeTag from 'primevue/tag';
import Hero from '../components/Hero.vue';

const runtimeConfig = useRuntimeConfig();

useSeoMeta({
  title: 'Pricing - Vismo',
  description: 'Vismo is a geospatial data platform that brings your projects to life',
  ogTitle: 'Pricing - Vismo',
  ogDescription: 'Vismo is a geospatial data platform that brings your projects to life',
  ogImage: joinPath(runtimeConfig.public.siteUrl, 'og.png'),
  ogUrl: runtimeConfig.public.siteUrl,
  twitterTitle: 'Pricing - Vismo',
  twitterDescription: 'Vismo is a geospatial data platform that brings your projects to life',
  twitterCard: 'summary_large_image',
  twitterImage: joinPath(runtimeConfig.public.siteUrl, 'twitter.png'),
});

// constants
const teamPlanStorage = '100GB';
const businessPlanStorage = '250GB';
const freePlanProjects = 3;
const teamPlanProjects = 100;
const businessPlanProjects = 500;

const getStartedUrl = joinPath(runtimeConfig.public.idUrl, '/signup');
const teamSignupUrl = computed(() =>
  signUpUrl(
    unref(showAnnual) ? 'plan_vismo_team_pricing_yearly' : 'plan_vismo_team_pricing_monthly',
  ),
);
const businessSignupUrl = computed(() =>
  signUpUrl(
    unref(showAnnual)
      ? 'plan_vismo_business_pricing_yearly'
      : 'plan_vismo_business_pricing_monthly',
  ),
);

function signUpUrl(pricingId: BillingPricingId) {
  return joinPath(runtimeConfig.public.portalUrl, `/billing-onboarding/${pricingId}`);
}

const showAnnual = ref(true);
const showIntroductoryOffer = ref(false);
const introductoryOfferEnd = format(new Date(), 'MMMM');
</script>

<template>
  <div class="pricing">
    <section class="pricing__hero">
      <h1 class="pricing__hero-title">Pricing - Find The Right Plan For You</h1>
      <ul class="pricing__hero-features">
        <li class="pricing__hero-feature">
          <Icon class="pricing__hero-feature-icon" icon="new_releases" :fill="true" /> Start Free -
          No Credit Card Required
        </li>
        <li class="pricing__hero-feature">
          <Icon class="pricing__hero-feature-icon" icon="new_releases" :fill="true" /> Predictable -
          Transparent Pricing
        </li>
        <li class="pricing__hero-feature">
          <Icon class="pricing__hero-feature-icon" icon="new_releases" :fill="true" /> Scalable -
          Grows With You
        </li>
      </ul>
    </section>

    <p
      v-if="showIntroductoryOffer"
      style="
        margin-top: -5rem;
        background: var(--p-button-help-background);
        color: var(--p-primary-contrast-color);
        padding: 1rem;
        text-align: center;
        font-size: 1.2rem;
      "
    >
      ⌛ Limited time offer: Get
      <strong style="font-weight: 800; text-decoration: underline; text-decoration-style: wavy"
        >50% off all plans!</strong
      >
      Sign up by end of {{ introductoryOfferEnd }}.
    </p>

    <div class="pricing__recurring">
      <PrimeSelectButton
        v-model="showAnnual"
        :options="[
          { label: 'Annual -20%', value: true },
          { label: 'Monthly', value: false },
        ]"
        option-label="label"
        option-value="value"
        size="large"
        :allow-empty="false"
        aria-label="Toggle Monthly/Annual Pricing"
      />
    </div>

    <div class="pricing__plans">
      <section class="pricing__plan">
        <div class="pricing__plan-header">
          <h2 class="pricing__plan-title">Free</h2>
          <p class="pricing__plan-price">£0 <span class="pricing__plan-price-per">/ month</span></p>
        </div>
        <div class="pricing__plan-content">
          <p class="pricing__plan-subtitle">For small projects or personal use.</p>
          <ul class="pricing__plan-features">
            <li
              v-tooltip.bottom="'Collaborate with up to 3 colleagues within your organisation.'"
              class="pricing__plan-feature"
            >
              <Icon class="pricing__plan-feature-icon" icon="new_releases" :fill="true" />
              <span class="pricing__plan-feature-tooltip">3 Users</span>
            </li>
            <li
              v-tooltip.bottom="'Create and/or own up to 3 projects.'"
              class="pricing__plan-feature"
            >
              <Icon class="pricing__plan-feature-icon" icon="new_releases" :fill="true" />
              <span class="pricing__plan-feature-tooltip">{{ freePlanProjects }} Projects</span>
            </li>
            <li
              v-tooltip.bottom="'Join and collaborate on as many external projects as you want.'"
              class="pricing__plan-feature"
            >
              <Icon class="pricing__plan-feature-icon" icon="new_releases" :fill="true" />
              <span class="pricing__plan-feature-tooltip">Join Unlimited Projects</span>
            </li>
          </ul>
          <NuxtLink :to="getStartedUrl" external>
            <PrimeButton class="pricing__plan-button" label="Sign Up" outlined />
          </NuxtLink>
        </div>
      </section>

      <section class="pricing__plan pricing__plan--popular">
        <div class="pricing__plan-header pricing__plan-header--popular">
          <PrimeTag class="pricing__plan-tag" severity="success" value="Popular" />
          <h2 class="pricing__plan-title">Team 🚀</h2>
          <p class="pricing__plan-price">
            <span v-if="showIntroductoryOffer" class="pricing__plan-price-strike">{{
              showAnnual ? '£16' : '£20'
            }}</span
            >{{ showAnnual ? '£16' : '£20' }}
            <span class="pricing__plan-price-per">/ month</span>
          </p>
          <p class="pricing__plan-price-description">Per User</p>
        </div>
        <div class="pricing__plan-content">
          <p class="pricing__plan-subtitle">For teams who want to level up.</p>
          <p class="pricing__plan-everything-in">
            Everything in <span class="pricing__plan-everything-in-plan">Free</span>, and:
          </p>
          <ul class="pricing__plan-features">
            <li
              v-tooltip.bottom="`Create and/or own up to ${teamPlanProjects} projects.`"
              class="pricing__plan-feature"
            >
              <Icon class="pricing__plan-feature-icon" icon="new_releases" :fill="true" />
              <span class="pricing__plan-feature-tooltip">{{ teamPlanProjects }} Projects</span>
            </li>
            <li
              v-tooltip.bottom="
                'Each user adds 100GB to your shared storage pool. For example, 5 users = 500GB.'
              "
              class="pricing__plan-feature"
            >
              <Icon class="pricing__plan-feature-icon" icon="new_releases" :fill="true" />
              <span class="pricing__plan-feature-tooltip"
                >{{ teamPlanStorage }} Storage Per User</span
              >
            </li>
            <li
              v-tooltip.bottom="'Invite up to 50 users to collaborate on any project you own.'"
              class="pricing__plan-feature"
            >
              <Icon class="pricing__plan-feature-icon" icon="new_releases" :fill="true" />
              <span class="pricing__plan-feature-tooltip">Free External Users Per Project</span>
            </li>
          </ul>
          <NuxtLink :to="teamSignupUrl" external>
            <PrimeButton class="pricing__plan-button" label="Subscribe" />
          </NuxtLink>
        </div>
      </section>

      <section class="pricing__plan">
        <div class="pricing__plan-header">
          <h2 class="pricing__plan-title">Business</h2>
          <p class="pricing__plan-price">
            <span v-if="showIntroductoryOffer" class="pricing__plan-price-strike">{{
              showAnnual ? '£24' : '£30'
            }}</span
            >{{ showAnnual ? '£24' : '£30' }}
            <span class="pricing__plan-price-per">/ month</span>
          </p>
          <p class="pricing__plan-price-description">Per User</p>
        </div>
        <div class="pricing__plan-content">
          <p class="pricing__plan-subtitle">The plan that means business.</p>
          <p class="pricing__plan-everything-in">
            Everything in <span class="pricing__plan-everything-in-plan">Team</span>, and:
          </p>
          <ul class="pricing__plan-features">
            <li
              v-tooltip.bottom="`Create and/or own up to ${businessPlanProjects} projects.`"
              class="pricing__plan-feature"
            >
              <Icon class="pricing__plan-feature-icon" icon="new_releases" :fill="true" />
              <span class="pricing__plan-feature-tooltip">{{ businessPlanProjects }} Projects</span>
            </li>
            <li
              v-tooltip.bottom="
                `Each user adds 250GB to your shared storage pool. For example, 4 users = 1TB.`
              "
              class="pricing__plan-feature"
            >
              <Icon class="pricing__plan-feature-icon" icon="new_releases" :fill="true" />
              <span class="pricing__plan-feature-tooltip"
                >{{ businessPlanStorage }} Storage Per User</span
              >
            </li>
            <li
              v-tooltip.bottom="'Invite up to 100 users to collaborate on any project you own.'"
              class="pricing__plan-feature"
            >
              <Icon class="pricing__plan-feature-icon" icon="new_releases" :fill="true" />
              <span class="pricing__plan-feature-tooltip">Free External Users Per Project</span>
            </li>
          </ul>
          <NuxtLink :to="businessSignupUrl" external>
            <PrimeButton class="pricing__plan-button" label="Subscribe" />
          </NuxtLink>
        </div>
      </section>

      <section class="pricing__plan pricing__plan--enterprise">
        <div class="pricing__plan-header">
          <h2 class="pricing__plan-title">Enterprise</h2>
          <p class="pricing__plan-price pricing__plan-price--small">Speak To Us</p>
          <NuxtLink :to="`mailto:${runtimeConfig.public.emailInfo}`" external>
            <PrimeButton class="pricing__plan-button" label="Request A Quote" outlined />
          </NuxtLink>
        </div>
        <div class="pricing__plan-content">
          <p class="pricing__plan-subtitle">For businesses with a big appetite.</p>
          <p class="pricing__plan-everything-in">
            Everything in <span class="pricing__plan-everything-in-plan">Business</span>, and:
          </p>
          <ul class="pricing__plan-features">
            <li class="pricing__plan-feature">
              <Icon class="pricing__plan-feature-icon" icon="new_releases" :fill="true" /> Unlimited
              Projects
            </li>
            <li class="pricing__plan-feature">
              <Icon class="pricing__plan-feature-icon" icon="new_releases" :fill="true" /> Unlimited
              Storage
            </li>
            <li
              v-tooltip.bottom="
                'Seamlessly and securely authenticate users via your corporate login system.'
              "
              class="pricing__plan-feature"
            >
              <Icon class="pricing__plan-feature-icon" icon="new_releases" :fill="true" />
              <span class="pricing__plan-feature-tooltip">SAML SSO</span>
            </li>
          </ul>
        </div>
      </section>
    </div>

    <div class="pricing__addons">
      <h2 class="pricing__addons-title">Addons</h2>
      <div class="pricing__addons-content">
        <section class="pricing__addon">
          <div class="pricing__addon-header">
            <h2 class="pricing__addon-title">Storage</h2>
            <p class="pricing__addon-price pricing__addon-price--small">
              £10
              <span class="pricing__addon-price-per">/ month</span>
            </p>
          </div>
          <div class="pricing__addon-content">
            <p class="pricing__addon-subtitle">Live storage for day to day activity.</p>
            <p class="pricing__addon-everything-in">
              Per additional <span class="pricing__addon-everything-in-plan">1TB</span> storage.
            </p>
          </div>
        </section>

        <section class="pricing__addon">
          <div class="pricing__addon-header">
            <h2 class="pricing__addon-title">Archive</h2>
            <p class="pricing__addon-price pricing__addon-price--small">
              £5
              <span class="pricing__addon-price-per">/ month</span>
            </p>
          </div>
          <div class="pricing__addon-content">
            <p class="pricing__addon-subtitle">
              Cheaper storage for long-term regulatory compliance.
            </p>
            <p class="pricing__addon-everything-in">
              Per additional <span class="pricing__addon-everything-in-plan">1TB</span> storage.
            </p>
          </div>
        </section>

        <section class="pricing__addon">
          <div class="pricing__addon-header">
            <h2 class="pricing__addon-title">SAML</h2>
            <p class="pricing__addon-price pricing__addon-price--small">
              £1k
              <span class="pricing__addon-price-per">/ year</span>
            </p>
          </div>
          <div class="pricing__addon-content">
            <p class="pricing__addon-subtitle">
              Enterprise-grade single sign-on with your existing identity provider.
            </p>
            <p class="pricing__addon-everything-in">
              <span class="pricing__addon-everything-in-plan">Unlimited Users</span>
            </p>
          </div>
        </section>

        <section class="pricing__addon">
          <div class="pricing__addon-header">
            <h2 class="pricing__addon-title">Monitoring</h2>
            <p class="pricing__addon-price pricing__addon-price--small">
              £1k
              <span class="pricing__addon-price-per">/ month</span>
            </p>
          </div>
          <div class="pricing__addon-content">
            <p class="pricing__addon-subtitle">Rail & Structural Monitoring.</p>
            <p class="pricing__addon-everything-in">
              Validate, Process and <span class="pricing__addon-everything-in-plan">Deliver</span>
            </p>
          </div>
        </section>
      </div>
      <NuxtLink :to="`mailto:${runtimeConfig.public.emailInfo}`" external>
        <PrimeButton label="Need Help? Speak To Us" outlined />
      </NuxtLink>
    </div>

    <section class="pricing__features">
      <h2 class="pricing__features-title">Features</h2>

      <Icon class="pricing__features-more" icon="double_arrow" />
      <div class="pricing__features-wrapper">
        <table>
          <thead>
            <tr>
              <th>Feature</th>
              <th>Free</th>
              <th>Team</th>
              <th>Business</th>
              <th>Enterprise</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <span
                  v-tooltip.bottom="'Collaborate with colleagues within your organisation.'"
                  class="pricing__features-row-tooltip"
                  >Users <Icon class="pricing__features-row-tooltip-icon" icon="info"
                /></span>
              </td>
              <td>3</td>
              <td>Pay per User</td>
              <td>Pay per User</td>
              <td>Unlimited</td>
            </tr>
            <tr>
              <td>
                <span
                  v-tooltip.bottom="
                    'Create and/or own a set number of projects. External projects are unlimited.'
                  "
                  class="pricing__features-row-tooltip"
                  >Projects <Icon class="pricing__features-row-tooltip-icon" icon="info"
                /></span>
              </td>
              <td>{{ freePlanProjects }}</td>
              <td>{{ teamPlanProjects }}</td>
              <td>{{ businessPlanProjects }}</td>
              <td>Unlimited</td>
            </tr>
            <tr>
              <td>
                <span
                  v-tooltip.bottom="
                    'Control how many users can join and collaborate on a single project.'
                  "
                  class="pricing__features-row-tooltip"
                  >Users Per Project <Icon class="pricing__features-row-tooltip-icon" icon="info"
                /></span>
              </td>
              <td>5</td>
              <td>50</td>
              <td>100</td>
              <td>Unlimited</td>
            </tr>
            <tr>
              <td>
                <span
                  v-tooltip.bottom="'Join and collaborate on external projects you don’t own.'"
                  class="pricing__features-row-tooltip"
                  >Join Projects <Icon class="pricing__features-row-tooltip-icon" icon="info"
                /></span>
              </td>
              <td>Unlimited</td>
              <td>Unlimited</td>
              <td>Unlimited</td>
              <td>Unlimited</td>
            </tr>
            <tr>
              <td>Storage</td>
              <td>
                <span
                  v-tooltip.bottom="
                    // eslint-disable-next-line vue/max-len
                    'Each user adds 1GB to your shared storage pool. For example, 3 users = 3GB.'
                  "
                  class="pricing__features-row-tooltip"
                  >1GB Per User <Icon class="pricing__features-row-tooltip-icon" icon="info"
                /></span>
              </td>
              <td>
                <span
                  v-tooltip.bottom="
                    // eslint-disable-next-line vue/max-len
                    'Each user adds 100GB to your shared storage pool. For example, 5 users = 500GB.'
                  "
                  class="pricing__features-row-tooltip"
                  >{{ teamPlanStorage }} Per User
                  <Icon class="pricing__features-row-tooltip-icon" icon="info"
                /></span>
              </td>
              <td>
                <span
                  v-tooltip.bottom="
                    // eslint-disable-next-line vue/max-len
                    'Each user adds 250GB to your shared storage pool. For example, 4 users = 1TB.'
                  "
                  class="pricing__features-row-tooltip"
                  >{{ businessPlanStorage }} Per User
                  <Icon class="pricing__features-row-tooltip-icon" icon="info"
                /></span>
              </td>
              <td>Unlimited</td>
            </tr>
            <tr>
              <td>Max File Size</td>
              <td>100MB</td>
              <td>100GB</td>
              <td>1TB</td>
              <td>1TB</td>
            </tr>
            <tr>
              <td>Max Files Per Project</td>
              <td>1000</td>
              <td>1000</td>
              <td>5000</td>
              <td>Unlimited</td>
            </tr>
            <tr>
              <td>File Versioning</td>
              <td colspan="4">Coming Soon...</td>
            </tr>
            <tr>
              <td>Preview Documents</td>
              <td v-for="i in [1, 2, 3, 4]" :key="i">
                <Icon class="pricing__features-row-icon" icon="new_releases" :fill="true" />
                <p class="pricing__features-row-small">
                  <span
                    v-tooltip.bottom="'View PDFs directly in a compatible browser.'"
                    class="pricing__features-row-tooltip"
                    >PDF</span
                  >
                </p>
              </td>
            </tr>
            <tr>
              <td>Preview Images</td>
              <td v-for="i in [1, 2, 3, 4]" :key="i">
                <Icon class="pricing__features-row-icon" icon="new_releases" :fill="true" />
                <p class="pricing__features-row-small">JPG, PNG, WEBP...</p>
              </td>
            </tr>
            <tr>
              <td>Preview Video</td>
              <td v-for="i in [1, 2, 3, 4]" :key="i">
                <Icon class="pricing__features-row-icon" icon="new_releases" :fill="true" />
                <p class="pricing__features-row-small">MP4, WEBM, OGV...</p>
              </td>
            </tr>
            <tr>
              <td>Preview CAD Files</td>
              <td v-for="i in [1, 2, 3, 4]" :key="i">
                <Icon class="pricing__features-row-icon" icon="new_releases" :fill="true" />
                <p class="pricing__features-row-small">DWG, DXF</p>
              </td>
            </tr>
            <tr>
              <td>Preview 3D Models</td>
              <td v-for="i in [1, 2, 3, 4]" :key="i">
                <Icon class="pricing__features-row-icon" icon="new_releases" :fill="true" />
                <p class="pricing__features-row-small">GLB, OBJ</p>
              </td>
            </tr>
            <tr>
              <td>Preview BIM Models</td>
              <td v-for="i in [1, 2, 3, 4]" :key="i">
                <Icon class="pricing__features-row-icon" icon="new_releases" :fill="true" />
                <p class="pricing__features-row-small">IFC</p>
              </td>
            </tr>
            <tr>
              <td>Point Clouds</td>
              <td v-for="i in [1, 2, 3, 4]" :key="i">
                <Icon class="pricing__features-row-icon" icon="new_releases" :fill="true" />
                <p class="pricing__features-row-small">LAS, LAZ</p>
              </td>
            </tr>
            <tr>
              <td>Photogrammetry</td>
              <td v-for="i in [1, 2, 3, 4]" :key="i">
                <Icon class="pricing__features-row-icon" icon="new_releases" :fill="true" />
                <p class="pricing__features-row-small">Tileset</p>
              </td>
            </tr>
            <tr>
              <td>Comments</td>
              <td v-for="i in [1, 2, 3, 4]" :key="i">
                <Icon class="pricing__features-row-icon" icon="new_releases" :fill="true" />
              </td>
            </tr>
            <tr>
              <td>Notifications</td>
              <td v-for="i in [1, 2, 3, 4]" :key="i">
                <Icon class="pricing__features-row-icon" icon="new_releases" :fill="true" />
              </td>
            </tr>
            <tr>
              <td>Annotations</td>
              <td v-for="i in [1, 2, 3, 4]" :key="i">
                <Icon class="pricing__features-row-icon" icon="new_releases" :fill="true" />
              </td>
            </tr>
            <tr>
              <td>Measurements</td>
              <td v-for="i in [1, 2, 3, 4]" :key="i">
                <Icon class="pricing__features-row-icon" icon="new_releases" :fill="true" />
              </td>
            </tr>
            <tr>
              <td>
                <span
                  v-tooltip.bottom="'Log in quickly with your Google or Microsoft account.'"
                  class="pricing__features-row-tooltip"
                  >Social SSO <Icon class="pricing__features-row-tooltip-icon" icon="info"
                /></span>
              </td>
              <td v-for="i in [1, 2, 3, 4]" :key="i">
                <Icon class="pricing__features-row-icon" icon="new_releases" :fill="true" />
              </td>
            </tr>
            <tr>
              <td>
                <span
                  v-tooltip.bottom="
                    'Seamlessly and securely authenticate users via your corporate login system.'
                  "
                  class="pricing__features-row-tooltip"
                  >SAML SSO <Icon class="pricing__features-row-tooltip-icon" icon="info"
                /></span>
              </td>
              <td v-for="i in [1, 2, 3]" :key="i">
                <Icon
                  class="pricing__features-row-icon pricing__features-row-icon--no"
                  icon="cancel"
                  :fill="true"
                />
              </td>
              <td>
                <Icon class="pricing__features-row-icon" icon="new_releases" :fill="true" />
              </td>
            </tr>
            <tr>
              <td>Support</td>
              <td>Basic</td>
              <td>Priority</td>
              <td>Priority</td>
              <td>Enterprise</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section class="pricing__faq">
      <div class="pricing__faq-content">
        <h2 class="pricing__faq-title">Pricing FAQ</h2>

        <PrimeAccordion value="0">
          <PrimeAccordionPanel value="0">
            <PrimeAccordionHeader>Is there a free trial available?</PrimeAccordionHeader>
            <PrimeAccordionContent>
              We have a free forever plan, so you can explore Vismo at your own pace. No credit card
              is required to get started.
            </PrimeAccordionContent>
          </PrimeAccordionPanel>
          <PrimeAccordionPanel value="1">
            <PrimeAccordionHeader>
              Can I change or cancel my subscription at any time?
            </PrimeAccordionHeader>
            <PrimeAccordionContent>
              Absolutely. You can upgrade, downgrade, or cancel your subscription at any time
              directly from your account settings. If you need assistance, our
              <NuxtLink :to="runtimeConfig.public.helpUrl" external>
                support team is here to help.
              </NuxtLink>
            </PrimeAccordionContent>
          </PrimeAccordionPanel>
          <PrimeAccordionPanel value="2">
            <PrimeAccordionHeader>
              Do you offer discounts for annual subscriptions or non-profits?
            </PrimeAccordionHeader>
            <PrimeAccordionContent>
              Yes, we provide discounts for annual subscriptions and special pricing for non-profit
              organisations.
              <NuxtLink :to="`mailto:${runtimeConfig.public.emailInfo}`" external>
                Get in touch with our team
              </NuxtLink>
              to learn more about these offers.
            </PrimeAccordionContent>
          </PrimeAccordionPanel>
          <PrimeAccordionPanel value="3">
            <PrimeAccordionHeader>
              Are there any additional costs beyond the subscription fee?
            </PrimeAccordionHeader>
            <PrimeAccordionContent>
              Our subscription plans cover all core features. However, optional add-ons, such as
              extra storage may incur additional charges. We'll always be transparent about costs.
            </PrimeAccordionContent>
          </PrimeAccordionPanel>
          <PrimeAccordionPanel value="4">
            <PrimeAccordionHeader> How does "Storage Per User" work? </PrimeAccordionHeader>
            <PrimeAccordionContent>
              Each user adds a set amount of storage to your shared storage pool for your entire
              organisation to use. For example, if you have 5 users on the Team plan, each user add
              100GB so you'll have 500GB of storage.
            </PrimeAccordionContent>
          </PrimeAccordionPanel>
          <PrimeAccordionPanel value="5">
            <PrimeAccordionHeader>Does unlimited mean unlimited?</PrimeAccordionHeader>
            <PrimeAccordionContent>
              Well, there are only a finite number of resources in the world, so no, it’s not
              technically unlimited. But rest assured, our plans are pretty generous!
            </PrimeAccordionContent>
          </PrimeAccordionPanel>
          <PrimeAccordionPanel value="6">
            <PrimeAccordionHeader>What if I have commitment issues?</PrimeAccordionHeader>
            <PrimeAccordionContent>
              No worries! Our free plan is like a no-strings-attached coffee date—no pressure, no
              credit card, and no awkward breakups. If you’re ready to take it to the next level,
              we’ve got plans for that too!
            </PrimeAccordionContent>
          </PrimeAccordionPanel>
        </PrimeAccordion>
      </div>
    </section>

    <Hero class="pricing__contact">
      <template #left>
        <img
          src="../assets/images/contact.webp"
          alt="Contact Us At Vismo"
          class="index__contact-image"
        />
      </template>
      <template #right>
        <div class="pricing__contact-content">
          <h2 class="pricing__contact-title">Need help with pricing?</h2>
          <p class="pricing__contact-text">
            Have a question or need help with pricing? Get in touch with us and we'll be happy to
            help.
          </p>
          <NuxtLink :to="`mailto:${runtimeConfig.public.emailInfo}`">
            <PrimeButton label="Get In Touch" />
          </NuxtLink>
        </div>
      </template>
    </Hero>
  </div>
</template>

<style scoped lang="scss">
@use '../assets/styles/_globals.scss' as g;

.pricing {
  display: flex;
  flex-direction: column;
  gap: 5rem;
  overflow: hidden;

  &__hero {
    position: relative;
    width: 100%;
    padding: 3rem 5rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2rem;
    background: light-dark(#38023b, var(--p-surface-900));
    color: white;
    overflow: hidden;

    @media (max-width: g.$mobile-breakpoint) {
      padding: 3rem 2rem;
    }

    &::before {
      content: '';
      position: absolute;
      left: 50%;
      top: 50%;
      width: 200%;
      height: 300%;
      background: url('../assets/images/icon-grid.svg') repeat center center;
      background-size: 250px;
      background-blend-mode: screen;
      transform: translateZ(0) translateX(-50%) translateY(-50%) rotate(11deg);
      will-change: transform;
      opacity: 0.4;
    }
  }

  &__hero-title {
    font-size: 2rem;
    font-weight: 800;
    text-align: center;
    z-index: 0;
  }

  &__hero-features {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    z-index: 0;
  }

  &__hero-feature {
    display: flex;
    flex-direction: row;
    gap: 0.5rem;
    align-items: center;
    font-size: 1.2rem;
    font-weight: 300;

    @media (max-width: g.$mobile-breakpoint) {
      font-size: 1rem;
    }
  }

  &__hero-feature-icon {
    font-size: 1.4rem;
    color: var(--p-green-500);

    @media (max-width: g.$mobile-breakpoint) {
      font-size: 1.2rem;
    }
  }

  &__recurring {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    margin: -2rem auto;
    --p-togglebutton-content-checked-background: var(--p-primary-color);
    --p-togglebutton-checked-color: var(--p-primary-contrast-color);
  }

  &__plans {
    width: 100%;
    max-width: g.$desktop-xl-breakpoint;
    margin: 0 auto;
    padding: 0 1rem;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 2rem;
    flex-direction: row;
    align-items: center;

    @media (max-width: g.$tablet-breakpoint) {
      display: flex;
      flex-direction: column;
    }
  }

  &__plan {
    width: 100%;
    display: flex;
    flex-direction: column;
    border: 1px solid light-dark(var(--p-neutral-200), var(--p-neutral-800));
    box-shadow: 0.5rem 0.5rem 1rem
      color-mix(in srgb, light-dark(var(--p-surface-900), var(--p-surface-0)) 4%, transparent);
    border-radius: 0.25rem;

    &--popular {
      border: 1px solid var(--p-green-500);
      box-shadow: 0.5rem 0.5rem 1rem color-mix(in srgb, var(--p-green-500) 4%, transparent);
    }

    &--enterprise {
      grid-column: 1 / span 3;
      flex-direction: row;
      width: max-content;
      margin: 0 auto;

      @media (max-width: g.$tablet-breakpoint) {
        flex-direction: column;
      }
    }
  }

  &__plan-header {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 1.5rem;
    background-color: light-dark(var(--p-surface-100), var(--p-surface-900));
    border-top-left-radius: 0.25rem;
    border-top-right-radius: 0.25rem;

    &--popular {
      background-color: var(--p-primary-color);
      color: var(--p-primary-contrast-color);
    }
  }

  &__plan-tag {
    display: inline-block;
    width: min-content;
    align-self: center;

    @media (prefers-color-scheme: dark) {
      background-color: var(--p-primary-300);
      color: var(--p-primary-contrast-color);
    }
  }

  &__plan-title {
    font-size: 1.3rem;
    font-weight: 800;
    align-self: center;
  }

  &__plan-price {
    display: flex;
    flex-direction: row;
    align-items: center;
    align-self: center;
    gap: 0.5rem;
    font-size: 4rem;
    font-weight: 800;

    &--small {
      font-size: 2rem;
    }
  }

  &__plan-price-strike {
    font-size: 1.8rem;
    text-decoration: line-through;
  }

  &__plan-price-per {
    font-size: 1.2rem;
    font-weight: 300;
  }

  &__plan-price-description {
    margin-top: -1rem;
    font-size: 0.8rem;
    align-self: center;
  }

  &__plan-content {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 1.5rem;
    border-radius: 0.25rem;
  }

  &__plan-subtitle {
    font-size: 1rem;
    font-weight: 300;
  }

  &__plan-everything-in {
    font-weight: 500;
  }

  &__plan-everything-in-plan {
    color: var(--p-primary-color);
  }

  &__plan-features {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  &__plan-feature {
    display: flex;
    flex-direction: row;
    gap: 0.5rem;
    align-items: center;
  }

  &__plan-feature-tooltip {
    text-decoration-color: var(--p-primary-color);
    text-decoration-line: underline;
    text-decoration-style: dotted;
    text-decoration-thickness: 0.1rem;
    text-underline-offset: 0.2rem;
  }

  &__plan-feature-icon {
    font-size: 1.2rem;
    color: var(--p-green-500);
  }

  &__plan-button {
    margin-top: 2rem;
    width: 100%;
  }

  &__addons {
    width: 100%;
    padding: 5rem 5rem;
    display: flex;
    flex-direction: column;
    gap: 2rem;
    align-items: center;
    background: light-dark(var(--p-surface-0), var(--p-surface-900));

    @media (max-width: g.$mobile-breakpoint) {
      padding: 3rem 1rem;
    }
  }

  &__addons-content {
    width: 100%;
    max-width: g.$desktop-xl-breakpoint;
    margin: 0 auto;
    padding: 0 1rem;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
    flex-direction: row;
    align-items: center;

    @media (max-width: g.$tablet-breakpoint) {
      display: flex;
      flex-direction: column;
    }
  }

  &__addons-title {
    text-align: center;
    font-size: 2rem;
    font-weight: 800;
  }

  &__addon {
    width: 100%;
    display: flex;
    flex-direction: row;
    border: 1px solid light-dark(var(--p-neutral-200), var(--p-neutral-800));
    box-shadow: 0.5rem 0.5rem 1rem
      color-mix(in srgb, light-dark(var(--p-surface-900), var(--p-surface-0)) 4%, transparent);
    border-radius: 0.25rem;
    margin: 0 auto;

    @media (max-width: g.$tablet-breakpoint) {
      flex-direction: column;
    }
  }

  &__addon-header {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 1.5rem;
    background-color: light-dark(var(--p-surface-100), var(--p-surface-900));
    border-top-left-radius: 0.25rem;
    border-top-right-radius: 0.25rem;

    &--popular {
      background-color: var(--p-primary-color);
      color: var(--p-primary-contrast-color);
    }
  }

  &__addon-tag {
    display: inline-block;
    width: min-content;
    align-self: center;

    @media (prefers-color-scheme: dark) {
      background-color: var(--p-primary-300);
      color: var(--p-primary-contrast-color);
    }
  }

  &__addon-title {
    font-size: 1.3rem;
    font-weight: 800;
    align-self: center;
  }

  &__addon-price {
    display: flex;
    flex-direction: row;
    align-items: center;
    align-self: center;
    gap: 0.5rem;
    font-size: 4rem;
    font-weight: 800;
    white-space: nowrap;

    &--small {
      font-size: 2rem;
    }
  }

  &__addon-price-strike {
    font-size: 1.8rem;
    text-decoration: line-through;
  }

  &__addon-price-per {
    font-size: 1.2rem;
    font-weight: 300;
  }

  &__addon-price-description {
    margin-top: -1rem;
    font-size: 0.8rem;
    align-self: center;
  }

  &__addon-content {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 1.5rem;
    border-radius: 0.25rem;
  }

  &__addon-subtitle {
    font-size: 1rem;
    font-weight: 300;
  }

  &__addon-everything-in {
    font-weight: 500;
  }

  &__addon-everything-in-plan {
    color: var(--p-primary-color);
  }

  &__addon-features {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  &__addon-feature {
    display: flex;
    flex-direction: row;
    gap: 0.5rem;
    align-items: center;
  }

  &__addon-feature-tooltip {
    text-decoration-color: var(--p-primary-color);
    text-decoration-line: underline;
    text-decoration-style: dotted;
    text-decoration-thickness: 0.1rem;
    text-underline-offset: 0.2rem;
  }

  &__addon-feature-icon {
    font-size: 1.2rem;
    color: var(--p-green-500);
  }

  &__addon-button {
    margin-top: 2rem;
    width: 100%;
  }

  &__faq {
    width: 100%;
    padding: 3rem 5rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    background: light-dark(var(--p-surface-0), var(--p-surface-900));

    @media (max-width: g.$mobile-breakpoint) {
      padding: 3rem 1rem;
    }
  }

  &__faq-content {
    width: 100%;
    max-width: g.$desktop-breakpoint;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  &__faq-title {
    text-align: center;
    font-size: 2rem;
    font-weight: 800;
  }

  &__features {
    width: 100%;
    padding: 1rem 5rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2rem;

    @media (max-width: g.$tablet-breakpoint) {
      padding-right: 1rem;
      padding-left: 1rem;
    }

    table {
      width: 100%;
      border-collapse: separate; // re-enable border-spacing for rounded
      border-spacing: 0;
      color: var(--p-text-color);
      margin: 0 auto;

      thead {
        th {
          border: 1px solid var(--p-primary-color);
          background: var(--p-primary-color);
          color: var(--p-primary-contrast-color);
          font-weight: 500;
          position: sticky;
          z-index: 2;
          top: 0;

          &:first-child {
            left: 0;
            z-index: 3;
            border-top-left-radius: 0.25rem;
            border-right: 1px solid var(--p-primary-400);
          }

          &:last-child {
            border-top-right-radius: 0.25rem;
          }
        }
      }

      th,
      td {
        padding: 1rem;
        text-align: center;
      }

      tbody {
        tr {
          td:first-child {
            position: sticky;
            left: 0;
            z-index: 1;
            border-left: 1px solid light-dark(var(--p-primary-200), var(--p-primary-800));
            border-right: 1px solid light-dark(var(--p-primary-200), var(--p-primary-800));
          }

          td:last-child {
            border-right: 1px solid light-dark(var(--p-primary-200), var(--p-primary-800));
          }

          &:last-child {
            td {
              border-bottom: 1px solid light-dark(var(--p-primary-200), var(--p-primary-800));
            }

            td:first-child {
              border-bottom-left-radius: 0.25rem;
            }

            td:last-child {
              border-bottom-right-radius: 0.25rem;
            }
          }
        }

        tr:nth-child(even) td {
          background: light-dark(var(--p-surface-100), var(--p-surface-900));
        }

        tr:nth-child(odd) td {
          background: light-dark(var(--p-surface-50), var(--p-surface-950));
        }
      }
    }
  }

  &__features-title {
    font-size: 2rem;
    font-weight: 800;
  }

  &__features-wrapper {
    width: 100%;
    max-width: g.$desktop-xl-breakpoint;

    @media (max-width: g.$mobile-breakpoint) {
      overflow: auto;
      max-height: 40rem;
    }
  }

  // this is a little hacky to position the arrow to the right of the table
  &__features-more {
    display: none;

    @media (max-width: g.$mobile-breakpoint) {
      display: block;
      position: absolute;
      right: 0;
      margin-top: 4.5rem;
      color: #42b883;
      color: var(--p-primary-color);
    }
  }

  &__features-row-icon {
    font-size: 1.2rem;
    color: var(--p-green-500);

    &--no {
      color: var(--p-red-500);
    }
  }

  &__features-row-small {
    font-size: 0.8rem;
  }

  &__features-row-tooltip {
    text-decoration-color: var(--p-primary-color);
    text-decoration-line: underline;
    text-decoration-style: dotted;
    text-decoration-thickness: 0.1rem;
    text-underline-offset: 0.2rem;
  }

  &__features-row-tooltip-icon {
    font-size: 0.8rem;
  }

  &__contact {
    --hero-padding: 6rem 11rem;
    //background: light-dark(var(--p-surface-0), var(--p-surface-900));
    --hero-left-width: 33%;
    --hero-right-width: 66%;

    @media (max-width: g.$desktop-breakpoint) {
      --hero-padding: 6rem 8rem;
    }

    @media (max-width: g.$mobile-breakpoint) {
      --hero-padding: 6rem;
    }
  }

  &__contact-content {
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 2rem;
    justify-content: center;
  }

  &__contact-title {
    font-size: 2.3rem;
    font-weight: 800;
  }

  &__contact-text {
    font-size: 1.2rem;
    font-weight: 300;
  }

  &__contact-image {
    display: block;
    width: 100%;
    object-fit: cover;

    @media (max-width: g.$mobile-breakpoint) {
      width: 50%;
      margin: 0 auto;
    }
  }
}
</style>
